// Analytics and error logging service

interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
  userId?: string;
  timestamp?: number;
}

interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  userId?: string;
  timestamp: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  context?: Record<string, any>;
}

class AnalyticsService {
  private isEnabled: boolean;
  private userId: string | null = null;
  private sessionId: string;
  private environment: string;
  private errorLog: ErrorReport[] = [];

  constructor() {
    this.isEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
    this.sessionId = this.generateSessionId();
    this.environment = import.meta.env.PROD ? 'production' : 'development';
    
    // Initialize analytics services
    this.initializeAnalytics();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeAnalytics() {
    if (!this.isEnabled) return;

    // Initialize Google Analytics
    if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
      this.initializeGoogleAnalytics();
    }
  }

  private initializeGoogleAnalytics() {
    const gaId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
    
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);

    // Initialize gtag
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = function() {
      (window as any).dataLayer.push(arguments);
    };
    
    (window as any).gtag('js', new Date());
    (window as any).gtag('config', gaId, {
      send_page_view: false, // We'll send page views manually
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    });
  }

  setUserId(userId: string) {
    this.userId = userId;
    
    if (this.isEnabled && (window as any).gtag) {
      (window as any).gtag('config', import.meta.env.VITE_GOOGLE_ANALYTICS_ID, {
        user_id: userId
      });
    }
  }

  trackEvent(event: AnalyticsEvent) {
    if (!this.isEnabled) return;

    const eventData = {
      ...event,
      userId: event.userId || this.userId,
      sessionId: this.sessionId,
      timestamp: event.timestamp || Date.now(),
      environment: this.environment
    };

    // Send to Google Analytics
    if ((window as any).gtag) {
      (window as any).gtag('event', event.name, {
        ...event.properties,
        user_id: eventData.userId,
        session_id: eventData.sessionId
      });
    }

    // Log in development
    if (import.meta.env.DEV) {
      console.log('Analytics Event:', eventData);
    }

    // Send to custom analytics endpoint (if configured)
    if (import.meta.env.VITE_ANALYTICS_ENDPOINT) {
      this.sendToCustomEndpoint(eventData);
    }
  }

  trackPageView(page: string, title?: string) {
    if (!this.isEnabled) return;

    const eventData = {
      page_title: title || document.title,
      page_location: window.location.href,
      page_path: page,
      user_id: this.userId,
      session_id: this.sessionId
    };

    if ((window as any).gtag) {
      (window as any).gtag('event', 'page_view', eventData);
    }

    if (import.meta.env.DEV) {
      console.log('Page View:', eventData);
    }
  }

  trackError(error: Error | ErrorReport, context?: Record<string, any>) {
    let errorReport: ErrorReport;

    if (error instanceof Error) {
      errorReport = {
        message: error.message,
        stack: error.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: this.userId || undefined,
        timestamp: Date.now(),
        severity: 'medium',
        context
      };
    } else {
      errorReport = {
        ...error,
        userId: error.userId || this.userId || undefined,
        context: { ...error.context, ...context }
      };
    }

    // Store error in local log
    this.errorLog.push(errorReport);
    
    // Keep only last 50 errors to prevent memory issues
    if (this.errorLog.length > 50) {
      this.errorLog.shift();
    }

    // Send to Google Analytics as exception
    if ((window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: errorReport.message,
        fatal: errorReport.severity === 'critical',
        user_id: this.userId,
        session_id: this.sessionId
      });
    }

    // Log error details
    if (import.meta.env.DEV) {
      console.error('Error Report:', errorReport);
    } else {
      // In production, log critical errors to console
      if (errorReport.severity === 'critical') {
        console.error('Critical Error:', errorReport.message);
      }
    }

    // Send to custom error reporting endpoint
    if (import.meta.env.VITE_ERROR_REPORTING_ENDPOINT) {
      this.sendErrorToEndpoint(errorReport);
    }
  }

  trackPerformance(metric: string, value: number, context?: Record<string, any>) {
    if (!this.isEnabled) return;

    const performanceData = {
      metric_name: metric,
      metric_value: value,
      user_id: this.userId,
      session_id: this.sessionId,
      ...context
    };

    if ((window as any).gtag) {
      (window as any).gtag('event', 'timing_complete', {
        name: metric,
        value: Math.round(value)
      });
    }

    if (import.meta.env.DEV) {
      console.log('Performance Metric:', performanceData);
    }
  }

  trackUserInteraction(action: string, element?: string, context?: Record<string, any>) {
    this.trackEvent({
      name: 'user_interaction',
      properties: {
        action,
        element,
        ...context
      }
    });
  }

  // Get recent errors for debugging
  getRecentErrors(): ErrorReport[] {
    return [...this.errorLog];
  }

  // Clear error log
  clearErrorLog() {
    this.errorLog = [];
  }

  // Export error log for manual review
  exportErrorLog(): string {
    return JSON.stringify(this.errorLog, null, 2);
  }

  private async sendToCustomEndpoint(data: any) {
    try {
      await fetch(import.meta.env.VITE_ANALYTICS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.warn('Failed to send analytics data:', error);
    }
  }

  private async sendErrorToEndpoint(errorReport: ErrorReport) {
    try {
      await fetch(import.meta.env.VITE_ERROR_REPORTING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(errorReport)
      });
    } catch (error) {
      console.warn('Failed to send error report:', error);
    }
  }
}

// Create singleton instance
export const analytics = new AnalyticsService();

// Convenience functions
export const trackEvent = (name: string, properties?: Record<string, any>) => {
  analytics.trackEvent({ name, properties });
};

export const trackPageView = (page: string, title?: string) => {
  analytics.trackPageView(page, title);
};

export const trackError = (error: Error | ErrorReport, context?: Record<string, any>) => {
  analytics.trackError(error, context);
};

export const trackPerformance = (metric: string, value: number, context?: Record<string, any>) => {
  analytics.trackPerformance(metric, value, context);
};

export const trackUserInteraction = (action: string, element?: string, context?: Record<string, any>) => {
  analytics.trackUserInteraction(action, element, context);
};

// Additional utility functions
export const getRecentErrors = () => analytics.getRecentErrors();
export const clearErrorLog = () => analytics.clearErrorLog();
export const exportErrorLog = () => analytics.exportErrorLog();

export default analytics; 